import { useState } from 'react';
import { Color, Size, feedTopic, keySegment, line, t, type FeedConnectorState, type FeedTagKind, type PlantFeedAnswer, type PlantFeedConnectAnswer, type StringKey } from '@bms2/shared';
import { AlertBanner, Button, CopyField, Pill, Select, TextInput } from '../../components/controls';
import { LoadFailure } from '../../components/composites';
import { FormFrame } from '../../components/forms';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { whenText } from '../../shell/viewerDates';
import { del, post, useApi } from './api';
import styles from './PlantFeedPanel.module.css';

/** The tile's routes (decision 12503), relative to the API root; each is registered in `apiPaths.test.ts`. */
export const PLANT_FEED_PATH = '/plant-feed/sites';
export function plantFeedPath(siteId: string): string {
  return `${PLANT_FEED_PATH}/${encodeURIComponent(siteId)}`;
}
export function plantFeedActPath(siteId: string, act: 'connect' | 'revoke' | 'maps'): string {
  return `${plantFeedPath(siteId)}/${act}`;
}
export function plantFeedMapPath(siteId: string, mapId: string): string {
  return `${plantFeedActPath(siteId, 'maps')}/${encodeURIComponent(mapId)}`;
}

/** Each state's status group: Online good, Silent warning, Offline error, Never inactive. */
const STATE_TONE: Readonly<Record<FeedConnectorState, 'good' | 'warning' | 'error' | 'inactive'>> = { Online: 'good', Silent: 'warning', Offline: 'error', Never: 'inactive' };

export interface PlantFeedPanelProps {
  siteId: string;
  siteName?: string;
  apiBaseUrl?: string;
}

/**
 * The Site page's Plant Feed tile (piece 28; decisions 12502, 12503; T3802).
 * Connect this Site makes the connector and shows the broker user, the password
 * and the HTTP key once. The tile then reads State and Since, Last Heard From,
 * the clock difference (amber past the setting), today's counts, the outages
 * with who saw each, the mapped tags with Remove, Import Devices and Revoke.
 * 🔨 A reader the route refuses sees nothing here, the way the site's other
 * gated panels read.
 */
export function PlantFeedPanel({ siteId, siteName = '', apiBaseUrl = '' }: PlantFeedPanelProps) {
  const fetched = useApi<PlantFeedAnswer>(plantFeedPath(siteId), apiBaseUrl);
  const [secrets, setSecrets] = useState<PlantFeedConnectAnswer | null>(null);
  const [notice, setNotice] = useState<{ tone: 'good' | 'error'; text: string } | null>(null);
  const [mapping, setMapping] = useState<{ tag: string; kind: FeedTagKind; key: number } | null>(null);
  const [busy, setBusy] = useState(false);
  if (fetched.denied) return null;
  if (fetched.state.kind === 'failed') return <LoadFailure state={fetched.state} onRetry={fetched.reload} />;
  if (fetched.state.kind !== 'ready') return <p className={styles.muted}>{t('plantFeed.loading')}</p>;
  const answer = fetched.state.data;
  const act = async (write: () => Promise<{ ok: boolean; message?: string }>, done: string | null) => {
    setBusy(true);
    const outcome = await write();
    setBusy(false);
    setNotice(outcome.ok ? (done ? { tone: 'good', text: done } : null) : { tone: 'error', text: outcome.message ?? '' });
    fetched.reload();
  };
  const connect = async () => {
    setBusy(true);
    const outcome = await post<PlantFeedConnectAnswer>(plantFeedActPath(siteId, 'connect'), {}, apiBaseUrl);
    setBusy(false);
    if (outcome.ok) setSecrets(outcome.data);
    else setNotice({ tone: 'error', text: outcome.message });
    fetched.reload();
  };
  return (
    <PlantFeedView
      answer={answer}
      siteName={siteName}
      secrets={secrets}
      notice={notice}
      busy={busy}
      onConnect={() => void connect()}
      onSecretsDone={() => setSecrets(null)}
      onDismissNotice={() => setNotice(null)}
      onRevoke={() => void act(() => post(plantFeedActPath(siteId, 'revoke'), {}, apiBaseUrl), t('plantFeed.revoked'))}
      onRemove={(mapId) => void act(() => del(plantFeedMapPath(siteId, mapId), apiBaseUrl), t('plantFeed.removed'))}
      onMap={(tag, kind) => setMapping({ tag, kind, key: Date.now() })}
      mapForm={
        mapping ? (
          <MapTagWindow
            key={mapping.key}
            answer={answer}
            initialTag={mapping.tag}
            initialKind={mapping.kind}
            onCancel={() => setMapping(null)}
            onSave={async (body) => {
              const outcome = await post<{ id: string }>(plantFeedActPath(siteId, 'maps'), body, apiBaseUrl);
              if (!outcome.ok) return outcome.message;
              setMapping(null);
              fetched.reload();
              return null;
            }}
          />
        ) : null
      }
    />
  );
}

export interface PlantFeedViewProps {
  answer: PlantFeedAnswer;
  siteName?: string;
  secrets?: PlantFeedConnectAnswer | null;
  notice?: { tone: 'good' | 'error'; text: string } | null;
  busy?: boolean;
  onConnect?: () => void;
  onSecretsDone?: () => void;
  onDismissNotice?: () => void;
  onRevoke?: () => void;
  onRemove?: (mapId: string) => void;
  onMap?: (tag: string, kind: FeedTagKind) => void;
  mapForm?: React.ReactNode;
}

/** The drawing alone, over an answer, so the Component Test Page draws each state with no route behind it. */
export function PlantFeedView({ answer, siteName = '', secrets = null, notice = null, busy = false, onConnect, onSecretsDone, onDismissNotice, onRevoke, onRemove, onMap, mapForm = null }: PlantFeedViewProps) {
  const zone = useViewerZone();
  const format = useDateFormat();
  const when = (at: string | null) => (at ? whenText(at, zone, format) : t('plantFeed.never'));
  // 🔨 An answer missing a list reads as an empty one, never a crash of the site page.
  const maps = Array.isArray(answer.maps) ? answer.maps : [];
  const unmapped = Array.isArray(answer.unmappedTags) ? answer.unmappedTags : [];
  const outages = Array.isArray(answer.outages) ? answer.outages : [];
  const connector = answer.connector;
  const banner = notice ? <AlertBanner tone={notice.tone} message={notice.text} {...(onDismissNotice ? { action: { label: t('modal.dismiss'), onAction: onDismissNotice } } : {})} /> : null;
  const secretsBlock = secrets ? (
    <div className={styles.secrets} data-plant-feed-secrets="true">
      <AlertBanner tone="good" message={t('plantFeed.connected')} />
      <CopyField label={t('plantFeed.siteKey')} value={secrets.siteKey} name="plant-feed-site-key" />
      <CopyField label={t('plantFeed.brokerUser')} value={secrets.brokerUser} name="plant-feed-broker-user" />
      <CopyField label={t('plantFeed.brokerPassword')} value={secrets.brokerPassword} name="plant-feed-broker-password" />
      <CopyField label={t('plantFeed.httpKey')} value={secrets.httpKey} name="plant-feed-http-key" />
      {onSecretsDone ? <Button size={Size.Small} color={Color.Navy} text={[t('plantFeed.secretsDone')]} actions={[onSecretsDone]} /> : null}
    </div>
  ) : null;
  if (!connector) {
    return (
      <div className={styles.panel} data-plant-feed="none">
        {banner}
        {secretsBlock}
        <div className={styles.row}>
          <span className={styles.muted}>{t('plantFeed.none')}</span>
          {onConnect ? <Button size={Size.Small} color={Color.Navy} text={[t('plantFeed.connect')]} actions={[onConnect]} disabled={busy} disabledReason={t('plantFeed.loading')} /> : null}
        </div>
      </div>
    );
  }
  const tone = STATE_TONE[connector.state] ?? 'inactive';
  const skew = connector.clockSkewSeconds;
  const lastOutage = outages[0] ?? null;
  const dot = '·';
  const todayLine = line(connector.readingsToday, dot, connector.faultsToday, t('plantFeed.todayCounts'));
  const outageLine = (lostAt: string, restoredAt: string | null) => line(when(lostAt), '–', restoredAt ? when(restoredAt) : t('plantFeed.outageOpen'));
  const unmappedLine = (row: { kind: FeedTagKind; count: number; lastAt: string }) => line(t(`plantFeed.kind.${keySegment(row.kind)}` as StringKey), dot, row.count, t('plantFeed.received'), dot, when(row.lastAt));
  return (
    <div className={styles.panel} data-plant-feed={connector.state}>
      {banner}
      {secretsBlock}
      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>{t('plantFeed.state')}</dt>
          <dd>
            <Pill text={t(`plantFeed.state.${keySegment(connector.state)}` as StringKey)} foreground={`var(--status-${tone}-text)`} background={`var(--status-${tone}-bg)`} size={Size.Small} />
          </dd>
        </div>
        <div className={styles.fact}>
          <dt>{t('plantFeed.since')}</dt>
          <dd>{when(connector.stateSince)}</dd>
        </div>
        <div className={styles.fact}>
          <dt>{t('plantFeed.lastHeard')}</dt>
          <dd data-plant-feed-last-heard="true">{when(connector.lastHeardAt)}</dd>
        </div>
        <div className={styles.fact}>
          <dt>{t('plantFeed.clock')}</dt>
          <dd className={connector.clockSkewWarns ? styles.warn : undefined} data-plant-feed-clock={connector.clockSkewWarns ? 'warn' : 'ok'}>
            {skew === null ? '—' : `${Math.abs(skew)}${t('plantFeed.clockSeconds')} ${skew >= 0 ? t('plantFeed.clockAhead') : t('plantFeed.clockBehind')}`}
          </dd>
        </div>
        <div className={styles.fact}>
          <dt>{t('plantFeed.today')}</dt>
          <dd>{todayLine}</dd>
        </div>
        <div className={styles.fact}>
          <dt>{t('plantFeed.brokerUser')}</dt>
          <dd>{connector.brokerUser}</dd>
        </div>
        <div className={styles.fact}>
          <dt>{t('plantFeed.brokerAddress')}</dt>
          <dd>{answer.brokerAddress ?? t('plantFeed.noBroker')}</dd>
        </div>
        <div className={styles.fact}>
          <dt>{t('plantFeed.topics')}</dt>
          <dd className={styles.code}>{feedTopic(connector.siteKey, 'readings').replace(/readings$/, '…')}</dd>
        </div>
      </dl>
      {connector.clockSkewWarns ? <p className={styles.warnLine} data-plant-feed-clock-warning="true">{t('plantFeed.clockWarn')}</p> : null}

      <section className={styles.section} aria-label={t('plantFeed.outages')}>
        <h4 className={styles.heading}>{t('plantFeed.outages')}</h4>
        {lastOutage === null ? (
          <p className={styles.muted}>{t('plantFeed.outagesNone')}</p>
        ) : (
          <ul className={styles.list} data-plant-feed-outages={outages.length}>
            {outages.map((outage) => (
              <li key={outage.id} className={styles.item}>
                <span>{outageLine(outage.lostAt, outage.restoredAt)}</span>
                <span className={styles.muted}>{t(`plantFeed.source.${keySegment(outage.source)}` as StringKey)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={styles.section} aria-label={t('plantFeed.maps')}>
        <div className={styles.row}>
          <h4 className={styles.heading}>{t('plantFeed.maps')}</h4>
          {onMap ? <Button size={Size.Small} color={Color.Navy} text={[t('plantFeed.mapTag')]} actions={[() => onMap('', 'Reading')]} /> : null}
        </div>
        {maps.length === 0 ? (
          <p className={styles.muted}>{t('plantFeed.mapsNone')}</p>
        ) : (
          <ul className={styles.list}>
            {maps.map((map) => (
              <li key={map.id} className={styles.item} data-plant-feed-map={map.tag}>
                <span className={styles.code}>{map.tag}</span>
                <span>{map.kind === 'Reading' ? `${map.assetNodeName} · ${map.attributeName ?? ''}${map.unitAbbreviation ? ` (${map.unitAbbreviation})` : ''}` : `${map.assetNodeName} · ${t('plantFeed.kind.fault')}`}</span>
                {onRemove ? <Button size={Size.Small} text={[t('plantFeed.remove')]} actions={[() => onRemove(map.id)]} disabled={busy} disabledReason={t('plantFeed.loading')} /> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={styles.section} aria-label={t('plantFeed.importDevices')}>
        <h4 className={styles.heading}>{t('plantFeed.importDevices')}</h4>
        {unmapped.length === 0 ? (
          <p className={styles.muted}>{t('plantFeed.importNone')}</p>
        ) : (
          <>
            <p className={styles.muted}>{t('plantFeed.importHint')}</p>
            <ul className={styles.list}>
              {unmapped.map((row) => (
                <li key={`${row.kind}-${row.tag}`} className={styles.item} data-plant-feed-unmapped={row.tag}>
                  <span className={styles.code}>{row.tag}</span>
                  <span className={styles.muted}>{unmappedLine(row)}</span>
                  {onMap ? <Button size={Size.Small} color={Color.Navy} text={[t('plantFeed.map')]} actions={[() => onMap(row.tag, row.kind)]} /> : null}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {onRevoke ? (
        <div className={styles.foot}>
          <Button size={Size.Small} color={Color.Destructive} text={[t('plantFeed.revoke')]} actions={[onRevoke]} destructive={{ subject: `${t('plantFeed.revokeSubject')} ${siteName || connector.siteKey}`, phrase: t('plantFeed.revokePhrase') }} disabled={busy} disabledReason={t('plantFeed.loading')} />
        </div>
      ) : null}
      {mapForm}
    </div>
  );
}

export interface MapTagBody {
  tag: string;
  kind: FeedTagKind;
  assetNodeId: string;
  attributeTypeId: string | null;
  unitId: string | null;
}

/** Map a Tag: the tag, Reading or Fault, the node, and for a reading the attribute and its unit. Keyed by its opener, so a reopened window starts fresh. */
export function MapTagWindow({ answer, initialTag, initialKind, onCancel, onSave }: { answer: PlantFeedAnswer; initialTag: string; initialKind: FeedTagKind; onCancel: () => void; onSave: (body: MapTagBody) => Promise<string | null> }) {
  const nodes = Array.isArray(answer.nodes) ? answer.nodes : [];
  const types = Array.isArray(answer.attributeTypes) ? answer.attributeTypes : [];
  const [tag, setTag] = useState(initialTag);
  const [kind, setKind] = useState<FeedTagKind>(initialKind);
  const [nodeId, setNodeId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const type = types.find((one) => one.id === typeId) ?? null;
  const nodeOptions = nodes.filter((node) => kind === 'Fault' || node.isComponent).map((node) => ({ value: node.id, label: node.name }));
  const save = async () => {
    if (!tag.trim()) return setRefusal(t('plantFeed.form.needsTag'));
    if (!nodeId) return setRefusal(t('plantFeed.form.needsNode'));
    if (kind === 'Reading' && !typeId) return setRefusal(t('plantFeed.form.needsAttribute'));
    setRefusal(await onSave({ tag: tag.trim(), kind, assetNodeId: nodeId, attributeTypeId: kind === 'Reading' ? typeId : null, unitId: kind === 'Reading' && unitId ? unitId : null }));
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('plantFeed.form.title')}
      onCancel={onCancel}
      dataForm="plant-feed-map"
      typed={tag !== initialTag || nodeId !== '' || typeId !== ''}
      act={{ label: t('plantFeed.form.save'), onAct: () => save() }}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
    >
      {nodes.length === 0 ? <p className={styles.warnLine}>{t('plantFeed.form.noNodes')}</p> : null}
      <TextInput size={Size.Large} label={t('plantFeed.form.tag')} value={tag} onChange={setTag} name="plant-feed-tag" hint={t('plantFeed.form.tagHint')} required />
      <Select size={Size.Small} label={t('plantFeed.form.kind')} value={kind} onChange={(value) => setKind(value === 'Fault' ? 'Fault' : 'Reading')} options={[{ value: 'Reading', label: t('plantFeed.kind.reading') }, { value: 'Fault', label: t('plantFeed.kind.fault') }]} required />
      <Select size={Size.Large} label={t('plantFeed.form.node')} value={nodeId} onChange={setNodeId} options={nodeOptions} placeholder={t('plantFeed.form.needsNode')} required />
      {kind === 'Reading' ? (
        <>
          <Select size={Size.Large} label={t('plantFeed.form.attribute')} value={typeId} onChange={(value) => { setTypeId(value); setUnitId(''); }} options={types.map((one) => ({ value: one.id, label: one.name }))} placeholder={t('plantFeed.form.needsAttribute')} required />
          <Select size={Size.Small} label={t('plantFeed.form.unit')} value={unitId} onChange={setUnitId} options={[{ value: '', label: t('plantFeed.form.unitNone') }, ...(type?.units ?? []).map((unit) => ({ value: unit.id, label: `${unit.name} (${unit.abbreviation})` }))]} hint={t('plantFeed.form.unitHint')} disabled={!type} />
        </>
      ) : null}
    </FormFrame>
  );
}
